import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { ArrowRight, FileText, AlertCircle, Search, Shield } from 'lucide-react';

const JSON_LD = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  "name": "Digital Asset Record Review for Family Law — VARfacts",
  "description": "VARfacts evaluates produced digital asset record sets in family law matters. Structured, source-cited reports for divorce proceedings, discovery, and disclosure.",
  "publisher": { "@type": "Organization", "name": "VARfacts" }
};

export default function FamilyLawPage() {

  useEffect(() => {
    document.title = 'Family Law Digital Asset Records — VARfacts';
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(JSON_LD);
    document.head.appendChild(script);
    return () => script.remove();
  }, []);

  return (
    <div className="min-h-screen bg-white" data-testid="family-law-page">
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-[#D4D4D0]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-16">
          <Link to="/" className="flex items-center">
            <img src="/10_VARfacts_logo_header_400x100.png" alt="VARfacts" className="h-8" />
          </Link>
          <div className="hidden md:flex items-center gap-6">
            <Link to="/virtual-asset-registry-framework" className="nav-link text-sm">VAR Framework</Link>
            <Link to="/family-law-digital-assets" className="nav-link text-sm">Family Law</Link>
            <Link to="/estate-digital-asset-records" className="nav-link text-sm">Estates</Link>
            <Link to="/contact" className="nav-link text-sm">Contact</Link>
          </div>
          <div className="flex items-center gap-3">
            <Button asChild className="btn-gold px-5 py-2 rounded-sm text-xs">
              <a href="mailto:secure.intake@varfacts.com">Contact Us</a>
            </Button>
          </div>
        </div>
      </nav>

      <div className="pt-16">
        <section className="hero-section grain-overlay py-20 md:py-28">
          <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-12 space-y-6">
            <p className="text-[#C5A55A]/80 text-sm tracking-wider uppercase">Family Law</p>
            <h1 className="font-heading text-4xl sm:text-5xl text-white leading-tight tracking-tight" data-testid="family-law-h1">
              Digital Asset Record Review for Family Law Matters
            </h1>
            <div className="w-16 h-0.5 bg-[#C5A55A]" />
            <p className="text-sm text-white/50 max-w-2xl leading-relaxed">
              In divorce and family law proceedings, digital asset records are often incomplete, inconsistent, or presented without context. VARfacts evaluates the produced record set and delivers a structured, source-cited VARfacts Report for counsel review.
            </p>
          </div>
        </section>

        <article className="max-w-4xl mx-auto px-6 md:px-12 py-16 md:py-24 space-y-16">
          <section className="space-y-4" data-testid="family-law-problem">
            <h2 className="font-heading text-2xl sm:text-3xl text-[#0B1F3F]">The Record Problem in Family Law</h2>
            <Separator className="bg-[#D4D4D0]" />
            <p className="text-[#64748B] leading-relaxed">
              Cryptocurrency and digital asset holdings frequently surface during divorce proceedings. One or both parties may produce exchange statements, wallet screenshots, transaction histories, or account summaries &mdash; often in conflicting formats, covering inconsistent date ranges, and with no standardized organization.
            </p>
            <p className="text-[#64748B] leading-relaxed">
              Without a structured evaluation, counsel cannot reliably determine what the records support, where they conflict, or what remains unproduced. Discovery disputes escalate, disclosure timelines extend, and settlement positions lack a documented foundation.
            </p>
          </section>

          <section className="space-y-6" data-testid="family-law-solution">
            <h2 className="font-heading text-2xl sm:text-3xl text-[#0B1F3F]">What VARfacts Delivers</h2>
            <Separator className="bg-[#D4D4D0]" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[
                { icon: FileText, title: 'Structured Evaluation', desc: 'The produced record set is organized, indexed, and evaluated under the VAR Framework.' },
                { icon: AlertCircle, title: 'Conflict Identification', desc: 'Discrepancies between produced records are documented with source citations.' },
                { icon: Search, title: 'Gap Analysis', desc: 'Missing records, unproduced periods, and incomplete account coverage are identified.' },
                { icon: Shield, title: 'Verification Status', desc: 'Each claim is classified under the Verification Status Standard based on record support.' },
              ].map((item) => (
                <div key={item.title} className="border border-[#D4D4D0] rounded-sm p-5 space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-[#0B1F3F] rounded-sm flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-4 h-4 text-[#C5A55A]" />
                    </div>
                    <h3 className="text-[#0B1F3F] font-semibold text-sm">{item.title}</h3>
                  </div>
                  <p className="text-sm text-[#64748B] leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="space-y-4" data-testid="family-law-use-cases">
            <h2 className="font-heading text-2xl sm:text-3xl text-[#0B1F3F]">Common Scenarios</h2>
            <Separator className="bg-[#D4D4D0]" />
            <div className="space-y-4">
              {[
                'One party claims cryptocurrency holdings but produced records are incomplete or inconsistent.',
                'Exchange statements cover different date ranges and cannot be cross-referenced without structured review.',
                'Wallet addresses are asserted but no produced documentation links them to a named party.',
                'Discovery responses include screenshots or partial exports with no source verification.',
              ].map((text) => (
                <div key={text} className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 rounded-full bg-[#C5A55A] mt-2 flex-shrink-0" />
                  <p className="text-[#64748B] leading-relaxed">{text}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="bg-[#F5F3EF] border border-[#D4D4D0] rounded-sm p-8 space-y-4" data-testid="family-law-cta">
            <h2 className="font-heading text-2xl text-[#0B1F3F]">Contact Us</h2>
            <p className="text-[#64748B] leading-relaxed">
              If you are managing a family law matter involving digital asset records, contact VARfacts to discuss the record set and scope of evaluation.
            </p>
            <div className="flex flex-wrap gap-4 pt-2">
              <Button asChild className="btn-primary-varfacts px-6 py-3 rounded-sm">
                <a href="mailto:secure.intake@varfacts.com">
                  Contact Us <ArrowRight className="ml-2 w-4 h-4" />
                </a>
              </Button>
            </div>
          </section>

          <footer className="pt-4 border-t border-[#D4D4D0]">
            <p className="text-xs text-[#94A3B8] italic leading-relaxed">
              Record-based evaluation and verification-status reporting only. Not legal, tax, or investment advice. No custody.
            </p>
          </footer>
        </article>
      </div>

      <footer className="bg-[#0B1F3F] py-12">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
            <div className="md:col-span-5 space-y-4">
              <img src="/01_VARfacts_logo_white_transparent.png" alt="VARfacts" className="h-8 opacity-90" />
            </div>
            <div className="md:col-span-3 space-y-3">
              <p className="text-white/40 text-xs font-semibold uppercase tracking-widest">Links</p>
              <div className="space-y-2">
                <Link to="/" className="block text-white/60 text-sm hover:text-[#C5A55A] transition-colors">Home</Link>
                <Link to="/virtual-asset-registry-framework" className="block text-white/60 text-sm hover:text-[#C5A55A] transition-colors">VAR Framework</Link>
                <Link to="/family-law-digital-assets" className="block text-white/60 text-sm hover:text-[#C5A55A] transition-colors">Family Law</Link>
                <Link to="/estate-digital-asset-records" className="block text-white/60 text-sm hover:text-[#C5A55A] transition-colors">Estates</Link>
                <Link to="/contact" className="block text-white/60 text-sm hover:text-[#C5A55A] transition-colors">Contact</Link>
              </div>
            </div>
            <div className="md:col-span-4 space-y-3">
              <p className="text-white/40 text-xs font-semibold uppercase tracking-widest">Contact</p>
              <div className="space-y-2">
                <p className="text-white/60 text-sm">secure.intake@varfacts.com</p>
                <p className="text-white/60 text-sm">Nashville, Tennessee</p>
                <a href="https://www.linkedin.com/company/varfacts" target="_blank" rel="noopener noreferrer" className="block text-white/60 text-sm hover:text-[#C5A55A] transition-colors">LinkedIn</a>
              </div>
            </div>
          </div>
          <Separator className="my-8 bg-white/10" />
          <p className="text-white/20 text-xs">&copy; 2026 VARfacts. All Rights Reserved.</p>
        </div>
      </footer>
    </div>
  );
}
